import { StyleSheet, css } from 'aphrodite'
import { useState } from 'react'
import { useUser } from '../../contexts/UserContext'
import { userService } from '../../services/userService'
import { StakeholderType } from '../../types/user'
import Input from '../Input'
import Modal from '../Modal'
import Select from '../Select'
import SubmitButton from '../SubmitButton'
import { TextArea } from '../TextArea'

interface ExternalInviteModalProps {
  onClose: () => void
  onSuccess: () => void
}

const STAKEHOLDER_TYPE_OPTIONS = [
  { value: StakeholderType.CONTRACTOR as string, label: 'Contractor' },
  { value: StakeholderType.BROKER as string, label: 'Broker' },
  { value: StakeholderType.SUB_HAULER as string, label: 'Sub Hauler' },
]

export const ExternalInviteModal: React.FC<ExternalInviteModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { user } = useUser()
  const defaultStakeholderType =
    user?.current_profile?.stakeholder_type || StakeholderType.CONTRACTOR

  const [email, setEmail] = useState('')
  const [stakeholderType, setStakeholderType] = useState<string>(
    defaultStakeholderType,
  )
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setError('Email is required')
      return
    }

    if (note.length > 250) {
      setError('Note cannot exceed 250 characters')
      return
    }

    try {
      setLoading(true)
      setError(null)

      await userService.sendExternalInvite({
        email: email.trim(),
        stakeholder_type: stakeholderType as StakeholderType,
        note: note.trim() || undefined,
      })

      onSuccess()
      onClose()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to send external invitation',
      )
    } finally {
      setLoading(false)
    }
  }

  const selectedStakeholderType = STAKEHOLDER_TYPE_OPTIONS.find(
    (option) => option.value === stakeholderType,
  )

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.header)}>
        <h2 className={css(styles.title)}>Invite External Member</h2>
        <button
          type="button"
          onClick={onClose}
          className={css(styles.closeButton)}
        >
          <i className="fa-solid fa-times" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className={css(styles.form)}>
        <div className={css(styles.field)}>
          <label className={css(styles.label)}>Email Address</label>
          <Input
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            placeholder="external@example.com"
            required
          />
        </div>

        <div className={css(styles.field)}>
          <label className={css(styles.label)}>
            Suggested Stakeholder Type (optional)
          </label>
          <p className={css(styles.hint)}>
            Suggest a stakeholder type for the invited user
          </p>
          <Select
            value={selectedStakeholderType}
            onChange={(option) =>
              setStakeholderType(option?.value || StakeholderType.CONTRACTOR)
            }
            options={STAKEHOLDER_TYPE_OPTIONS}
            placeholder="Select stakeholder type"
          />
        </div>

        <div className={css(styles.field)}>
          <label className={css(styles.label)}>
            Note (optional)
            <span className={css(styles.charCount)}>{note.length}/250</span>
          </label>
          <TextArea
            value={note}
            onChange={(value: string) => setNote(value)}
            placeholder="Add a personal note to the invitation..."
            maxLength={250}
            rows={4}
          />
        </div>

        {error && <div className={css(styles.error)}>{error}</div>}

        <div className={css(styles.actions)}>
          <button
            type="button"
            onClick={onClose}
            className={css(styles.cancelButton)}
            disabled={loading}
          >
            Cancel
          </button>
          <SubmitButton canSubmit={!loading} text="Send Invitation" />
        </div>
      </form>
    </Modal>
  )
}

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 20px 0 20px',
    borderBottom: '1px solid #E5E7EB',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#6B7280',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      color: '#1F2937',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '0 20px 20px 20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  charCount: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: 400,
  },
  hint: {
    fontSize: '12px',
    color: '#6B7280',
    margin: 0,
  },
  error: {
    color: '#EF4444',
    fontSize: '14px',
    padding: '8px',
    backgroundColor: '#FEF2F2',
    borderRadius: '4px',
    border: '1px solid #FECACA',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6B7280',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#F9FAFB',
      color: '#1F2937',
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
})
